import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toolsAPI } from '../lib/api';
import ManagePresetsModal from '../components/tools/ManagePresetsModal';

const TOOL_KEY = 'standings-updater';
const FIELD_CLASS = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';

function emptySource() { return { championship_url: '', sheet_url: '' }; }

function SavePresetInline({ initialName = '', onSave, onCancel }) {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handle() {
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await onSave(name.trim());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save preset');
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 flex items-center gap-2">
      <input
        autoFocus
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Preset name (e.g. MXGB 2026)"
        className={`${FIELD_CLASS} flex-1`}
      />
      <button
        onClick={handle}
        disabled={saving || !name.trim()}
        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded"
      >
        {saving ? 'Saving…' : 'Save preset'}
      </button>
      <button onClick={onCancel} className="px-3 py-2 text-sm text-gray-400 hover:text-white">
        Cancel
      </button>
      {error && <span className="text-xs text-red-400">{error}</span>}
    </div>
  );
}

export default function StandingsUpdaterPage() {
  const [tool, setTool] = useState(null);
  const [serviceAccountEmail, setServiceAccountEmail] = useState(null);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [showManageModal, setShowManageModal] = useState(false);

  const [sources, setSources] = useState([emptySource()]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);

  function loadAll() {
    Promise.all([toolsAPI.list(), toolsAPI.presets(TOOL_KEY)])
      .then(([listRes, presetsRes]) => {
        const t = (listRes.data.tools || []).find(x => x.key === TOOL_KEY);
        setTool(t || null);
        setServiceAccountEmail(listRes.data.service_account_email || null);
        setPresets(presetsRes.data || []);
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load tool'));
  }

  useEffect(() => { loadAll(); }, []);

  function applyPreset(presetId) {
    setSelectedPresetId(presetId);
    if (!presetId) {
      setSources([emptySource()]);
      return;
    }
    const p = presets.find(x => x.id === presetId);
    if (!p) return;
    const cfg = p.config || {};
    setSources((cfg.sources || []).length ? cfg.sources : [emptySource()]);
    setResult(null);
    setError('');
    setShowSavePreset(false);
  }

  function updateSource(i, field, value) {
    setSources(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  }
  function addSource() { setSources(prev => [...prev, emptySource()]); }
  function removeSource(i) { setSources(prev => prev.filter((_, idx) => idx !== i)); }

  const canRun = !running && sources.every(s => s.championship_url.trim() && s.sheet_url.trim());

  async function handleRun() {
    setRunning(true);
    setError('');
    setResult(null);
    setShowSavePreset(false);
    try {
      const res = await toolsAPI.runStandingsUpdater({
        sources: sources.map(s => ({
          championship_url: s.championship_url.trim(),
          sheet_url: s.sheet_url.trim(),
        })),
        preset_id: selectedPresetId || null,
      });
      const httpStatus = res.status;
      const data = res.data || {};
      if (httpStatus >= 400 && !data.results) {
        setError(data.error || `Run failed (${httpStatus})`);
      } else {
        setResult({ ...data, http_status: httpStatus });
      }
      loadAll();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  }

  async function handleSavePreset(name) {
    await toolsAPI.createPreset(TOOL_KEY, {
      name,
      sources: sources.map(s => ({
        championship_url: s.championship_url.trim(),
        sheet_url: s.sheet_url.trim(),
      })),
    });
    setShowSavePreset(false);
    loadAll();
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <Link to="/tools" className="text-xs text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-1 mb-4">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Tools
      </Link>

      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">{tool?.name || 'Google Sheet Champ Standings Updater'}</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          {tool?.description || 'Scrape championship standings from Speedhive into Google Sheets'}
        </p>
        {tool?.last_run_at && (
          <p className="text-xs text-gray-600 mt-1">
            Last run: {new Date(tool.last_run_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Service-account share notice */}
      <div className="mb-5 px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded text-xs text-gray-400 leading-relaxed">
        Each destination Google Sheet must be shared with{' '}
        {serviceAccountEmail ? (
          <code className="text-gray-200">{serviceAccountEmail}</code>
        ) : (
          <span className="text-amber-500">the TNL Tools service account</span>
        )}{' '}
        as Editor before this tool can write to it.
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>
      )}

      {/* Preset bar */}
      <div className="mb-5 flex items-center gap-2">
        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Preset</label>
        <select
          value={selectedPresetId}
          onChange={e => applyPreset(e.target.value)}
          className="bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
        >
          <option value="">(no preset)</option>
          {presets.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button
          onClick={() => setShowManageModal(true)}
          className="text-xs text-blue-500 hover:text-blue-400 transition-colors"
        >
          Manage presets
        </button>
      </div>

      {/* Pairs */}
      <div className="mb-5">
        <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
          Championship → Sheet tab pairs
        </label>
        <p className="text-xs text-gray-600 mb-3 leading-relaxed">
          For each championship: paste its <strong>Speedhive standings URL</strong>, and the <strong>Google Sheet tab URL</strong> you want it written to. Open the destination tab in Sheets and copy the URL — it'll end with <code className="text-gray-500">#gid=...</code>, which tells the tool exactly which tab to overwrite.
        </p>

        <div className="space-y-3">
          {sources.map((src, i) => (
            <div key={i} className="bg-[#0d0d0d] border border-[#2a2a2a] rounded p-3 relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Pair {i + 1}</span>
                <button
                  type="button"
                  onClick={() => removeSource(i)}
                  disabled={sources.length === 1 || running}
                  className="text-gray-600 hover:text-red-400 disabled:opacity-30"
                  title="Remove this pair"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">Speedhive URL</label>
                  <input
                    placeholder="https://speedhive.mylaps.com/.../championships/..."
                    value={src.championship_url}
                    onChange={e => updateSource(i, 'championship_url', e.target.value)}
                    disabled={running}
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">Google Sheet tab URL</label>
                  <input
                    placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=..."
                    value={src.sheet_url}
                    onChange={e => updateSource(i, 'sheet_url', e.target.value)}
                    disabled={running}
                    className={FIELD_CLASS}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addSource}
          disabled={running}
          className="mt-3 text-xs text-blue-500 hover:text-blue-400 transition-colors disabled:opacity-50"
        >
          + Add another championship
        </button>
      </div>

      {/* Run + save preset */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={handleRun}
          disabled={!canRun}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
        >
          {running ? 'Running…' : 'Run update'}
        </button>

        {!selectedPresetId && !showSavePreset && (
          <button
            type="button"
            onClick={() => setShowSavePreset(true)}
            disabled={running || !sources.every(s => s.championship_url.trim() && s.sheet_url.trim())}
            className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#222222] disabled:opacity-40 border border-[#2a2a2a] text-gray-300 text-sm font-medium rounded transition-colors"
            title="Save these pairs as a preset"
          >
            Save as preset
          </button>
        )}

        {selectedPresetId && (
          <button
            type="button"
            onClick={async () => {
              try {
                await toolsAPI.updatePreset(TOOL_KEY, selectedPresetId, {
                  sources: sources.map(s => ({
                    championship_url: s.championship_url.trim(),
                    sheet_url: s.sheet_url.trim(),
                  })),
                });
                loadAll();
              } catch (err) {
                setError(err.response?.data?.error || 'Failed to update preset');
              }
            }}
            disabled={running || !sources.every(s => s.championship_url.trim() && s.sheet_url.trim())}
            className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#222222] disabled:opacity-40 border border-[#2a2a2a] text-gray-300 text-sm font-medium rounded transition-colors"
            title="Update the selected preset with these pairs"
          >
            Update preset
          </button>
        )}
      </div>

      {/* Inline save-preset prompt — shown above Run when triggered pre-run */}
      {showSavePreset && !result && (
        <div className="mb-6 bg-[#111111] border border-[#2a2a2a] rounded p-4">
          <div className="text-xs text-gray-400 mb-2">Save these {sources.length} pair{sources.length !== 1 ? 's' : ''} as a preset</div>
          <SavePresetInline
            onSave={handleSavePreset}
            onCancel={() => setShowSavePreset(false)}
          />
        </div>
      )}

      {/* Run result */}
      {result && (
        <div className="mb-5 bg-[#111111] border border-[#2a2a2a] rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
              result.status === 'success' ? 'bg-green-900/30 text-green-400' :
              result.status === 'partial' ? 'bg-amber-900/30 text-amber-400' :
              'bg-red-900/30 text-red-400'
            }`}>
              {result.status}
            </div>
            <div className="text-sm text-white">
              {result.success_count} of {result.success_count + result.fail_count} tab{(result.success_count + result.fail_count) !== 1 ? 's' : ''} written
            </div>
          </div>

          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#2a2a2a] text-gray-500">
                <th className="text-left py-2 font-medium">Tab</th>
                <th className="text-left py-2 font-medium">Championship</th>
                <th className="text-left py-2 font-medium">Rows</th>
                <th className="text-left py-2 font-medium">Status</th>
                <th className="text-left py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {(result.results || []).map((r, i) => (
                <tr key={i} className="border-b border-[#1a1a1a] last:border-0">
                  <td className="py-2 text-white">{r.tab_name || '—'}</td>
                  <td className="py-2 text-gray-400 truncate max-w-[240px]">
                    {r.status === 'success' ? (r.championship_name || '—') : '—'}
                  </td>
                  <td className="py-2 text-gray-400 font-mono">
                    {r.status === 'success' ? r.rows_written : '—'}
                  </td>
                  <td className="py-2">
                    {r.status === 'success' ? (
                      <span className="text-green-400">Success</span>
                    ) : (
                      <span className="text-red-400" title={r.error}>{r.error || 'Failed'}</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <a href={r.sheet_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-400 text-xs">
                      Open ↗
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Save as preset */}
          {!selectedPresetId && (result.status === 'success' || result.status === 'partial') && (
            <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
              {showSavePreset ? (
                <SavePresetInline
                  onSave={handleSavePreset}
                  onCancel={() => setShowSavePreset(false)}
                />
              ) : (
                <button
                  onClick={() => setShowSavePreset(true)}
                  className="text-xs text-blue-500 hover:text-blue-400 transition-colors"
                >
                  Save these settings as a preset
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showManageModal && (
        <ManagePresetsModal
          presets={presets}
          onClose={() => setShowManageModal(false)}
          onChanged={loadAll}
        />
      )}
    </div>
  );
}
